import React, { useContext, useEffect, useState, useRef } from "react";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import config from "../../../config/Config";
import { useNavigate } from "react-router-dom";
import { FormContext, UserRoleContext } from "../../../context/FormContext";
import Pagination from "@mui/material/Pagination";
import Skeleton from "@mui/material/Skeleton";
import Box from "@mui/material/Box";
import "../../../styles/pageStyles/applicationListing/applicationListingTable.css";
import TuneIcon from "@mui/icons-material/Tune";
import InlineFilterPopup from "./InlineFilterPopup";
import ApplicationListingColumnSelector from "./ApplicationListingColumnSelector";
import {
  reorderColumnsByDrag,
  COLUMN_FILTER_CONFIG,
  getColumnCellValue,
  getHeaderStickyStyle,
  getBodyStickyStyle,
  DEFAULT_COLUMN_WIDTHS,
  clampColumnWidth,
  getColumnWidth,
  getMinColumnWidth,
  mergeColumnWidthIntoStyle,
} from "./applicationListingColumnDnD";

var localTemp = [];

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#f4f5f6",
    color: "black",
    fontSize: "14px",
    fontWeight: 600,
    fontFamily: "proxima-regular !important",
    padding: "8px",
    textAlign: "center",
    borderRight: "1px solid #e8e8e8",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: "14px",
    fontFamily: "proxima-regular !important",
    padding: "8px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  [`&.${tableCellClasses.head}:last-of-type`]: {
    borderRight: "none",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: "#f4f5f6",
  },
  "&:last-child td, &:last-child th": {
    borderBottom: 0,
  },
}));

/** Column header — native HTML tooltip */
const ColumnHeaderLabel = ({ label }) => (
  <span
    title={label || undefined}
    style={{
      minWidth: 0,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      flex: "1 1 auto",
    }}
  >
    {label}
  </span>
);

/** Table cell value — native HTML tooltip */
const CellWithTitle = ({ value, title, className }) => {
  const text =
    value === null || value === undefined || String(value).trim() === ""
      ? "-"
      : String(value);
  const tip = title != null && String(title).trim() !== "" ? String(title) : text;
  const showTitle = tip && tip !== "-" && tip !== "NA" && tip !== "";

  return (
    <span
      className={className || "app-listing-truncated-cell"}
      title={showTitle ? tip : undefined}
      style={{
        display: "block",
        width: "100%",
        minWidth: 0,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {text}
    </span>
  );
};

const DATE_TIME_COLUMN_IDS = [
  "created_date",
  "updated_date",
  "purchase_date",
  "home_owner_approval_date",
];

/** Body cells: keep Delete, App Number, and boolean values centered; left-align the rest */
const getBodyCellAlign = (colId) => {
  if (colId === "delete" || colId === "application_number") return "center";
  if (COLUMN_FILTER_CONFIG[colId]?.fieldType === "boolean") return "center";
  return "left";
};

const getDateRawValue = (colId, row) => {
  switch (colId) {
    case "created_date":
      return row.created_date;
    case "updated_date":
      return row.updated_date;
    case "purchase_date":
      return row.purchase_date;
    case "home_owner_approval_date":
      return row.homeowner_approved_date;
    default:
      return null;
  }
};

/** Date column — date in cell; native title shows full datetime */
const DateTimeCell = ({ dateValue, emptyLabel = "-" }) => {
  if (!dateValue) {
    return <span>{emptyLabel}</span>;
  }

  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) {
    return <span>{emptyLabel}</span>;
  }

  const dateOnly = parsed.toLocaleDateString("en-US");
  const dateTime = parsed.toLocaleString("en-US", {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });

  return (
    <CellWithTitle
      value={dateOnly}
      title={dateTime}
      className="app-listing-truncated-cell app-listing-date-cell"
    />
  );
};

const SORT_ICON_ACTIVE = "#067de2";
const SORT_ICON_IDLE = "rgb(85, 85, 85)";

const ColumnSortToggle = ({ colId, sortField, sortDirection, onSort }) => {
  const isActive = sortField === colId;
  const isAsc = isActive && sortDirection === "asc";
  const isDesc = isActive && sortDirection === "desc";

  return (
    <Box
      component="span"
      aria-label={
        isAsc
          ? "Sorted ascending"
          : isDesc
            ? "Sorted descending"
            : "Sortable"
      }
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 14,
        height: 14,
        flexShrink: 0,
        lineHeight: 1,
        // Pointer events are handled by the parent sort zone; the icon itself
        // is purely visual so it never blocks or double-fires clicks
        pointerEvents: "none",
      }}
    >
      <svg
        width="14"
        height="14"
        viewBox="0 0 14 14"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <path
          d="M7 2.25v3.5"
          stroke={isAsc ? SORT_ICON_ACTIVE : SORT_ICON_IDLE}
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <path
          d="M4.75 4.75L7 2.25L9.25 4.75"
          stroke={isAsc ? SORT_ICON_ACTIVE : SORT_ICON_IDLE}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M7 8.25v3.5"
          stroke={isDesc ? SORT_ICON_ACTIVE : SORT_ICON_IDLE}
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <path
          d="M4.75 10.75L7 13.25L9.25 10.75"
          stroke={isDesc ? SORT_ICON_ACTIVE : SORT_ICON_IDLE}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </Box>
  );
};

// Frontend column id → backend preference key
const COLUMN_BACKEND_KEY = {
  application_number: "trade_in_application_number",
  originator_name: "originator_name",
  borrower_name: "borrower_name",
  property_address: "property_address",
  created_by: "created_by",
  created_date: "created_date",
  updated_date: "updated_date",
  cx_specialist_name: "cx_specialist",
  application_status_name: "application_status",
  is_test_app: "is_test_app",
  submitted_to_crm: "submitted_to_crm",
  is_submitted_by_partner_api: "is_submitted_by_partner_api",
  is_upside_submission: "is_upside_submission",
  is_upside_interested: "is_upside_interested",
  property_type: "property_type",
  company_name: "submitter_company_name",
  program_name: "calque_program",
  purchase_price: "purchase_price",
  purchase_date: "purchase_date",
  owner_estimated_value: "owner_estimated_home_value",
  borrower_email: "borrower_email",
  co_borrower_name: "co_borrower_name",
  co_borrower_email: "co_borrower_email",
  loan_officer_name: "loan_officer_name",
  loan_officer_email: "loan_officer_email",
  real_estate_agent_name: "real_estate_agent_name",
  real_estate_agent_email: "real_estate_agent_email",
  partner_loan_number: "external_reference_number",
  initial_submiter_role: "submitter_role",
  initiail_submiter_name: "submitter_name",
  initial_submiter_email: "submitter_email",
  home_owner_approved: "ho_approved",
  home_owner_approval_date: "homeowner_approved_date",
  is_primary_residence: "is_primary_residence",
  equity_unlock_requested: "equity_unlock_requested",
};

/** API sort ordering: ascending = N, descending = -N (query param `ordering`) */
const COLUMN_ORDERING_KEY = {
  application_number: 1,
  originator_name: 2,
  borrower_name: 3,
  property_address: 4,
  created_by: 5,
  created_date: 6,
  updated_date: 7,
  cx_specialist_name: 8,
  application_status_name: 9,
  is_test_app: 10,
  submitted_to_crm: 11,
  is_submitted_by_partner_api: 12,
  is_upside_submission: 13,
  property_type: 14,
  company_name: 15,
  program_name: 16,
  purchase_price: 17,
  purchase_date: 18,
  owner_estimated_value: 19,
  borrower_email: 20,
  co_borrower_name: 21,
  co_borrower_email: 22,
  loan_officer_name: 23,
  loan_officer_email: 24,
  real_estate_agent_name: 25,
  real_estate_agent_email: 26,
  partner_loan_number: 27,
  initial_submiter_role: 28,
  initiail_submiter_name: 29,
  initial_submiter_email: 30,
  home_owner_approved: 31,
  home_owner_approval_date: 32,
  is_primary_residence: 33,
  is_upside_interested: 36,
  equity_unlock_requested: 35,
};

/** Build API payload: visible cols get order_index 1..n; hidden only is_visible:false */
const buildApplicationListingColumnPrefs = (columns, columnWidths = {}) => {
  const prefs = {};
  const ordered = [...columns]
    .filter((c) => c.id !== "delete")
    .sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0));

  let orderIndex = 1;
  ordered.forEach((col) => {
    const key = COLUMN_BACKEND_KEY[col.id];
    if (!key) return;
    const columnWidth = getColumnWidth(col.id, columnWidths);
    if (col.checked) {
      prefs[key] = {
        is_visible: true,
        order_index: orderIndex,
        column_width: columnWidth,
      };
      orderIndex += 1;
    } else {
      prefs[key] = {
        is_visible: false,
        column_width: columnWidth,
      };
    }
  });
  return prefs;
};

const buildColumnWidthsFromPreferences = (prefs) => {
  if (!prefs) return {};
  const widths = {};
  Object.entries(COLUMN_BACKEND_KEY).forEach(([colId, backendKey]) => {
    const width = prefs[backendKey]?.column_width;
    if (typeof width === "number" && width > 0) {
      widths[colId] = clampColumnWidth(width, colId);
    }
  });
  return widths;
};

// Always in table; always checked — Delete hidden from drawer; 5 core cols shown locked in drawer
const MANDATORY_COLUMN_IDS = [
  "delete",
  "application_number",
  "originator_name",
  "borrower_name",
  "property_address",
  "created_by",
];

// Only these stay left of preference order (Delete + sticky App Number)
const FIXED_LEADING_COLUMN_IDS = ["delete", "application_number"];

const getListingColumnPrefs = () => {
  try {
    const userInfo = JSON.parse(localStorage.getItem("user_info") || "{}");
    return userInfo?.preferences?.application_listing_columns || null;
  } catch {
    return null;
  }
};

const getListingRowPerPage = (fallback = 8) => {
  try {
    const userInfo = JSON.parse(localStorage.getItem("user_info") || "{}");
    const n = Number(userInfo?.preferences?.application_listing_row_per_page);
    return Number.isFinite(n) && n > 0 ? n : fallback;
  } catch {
    return fallback;
  }
};

// Single source of truth for ALL columns (mandatory + optional).
const INITIAL_COLUMNS = [
  { id: "delete", label: "Delete", mandatory: true, checked: true },
  {
    id: "application_number",
    label: "Trade In Application Number",
    mandatory: true,
    checked: true,
  },
  {
    id: "originator_name",
    label: "Originator Name",
    mandatory: true,
    checked: true,
  },
  {
    id: "borrower_name",
    label: "Borrower Name",
    mandatory: true,
    checked: true,
  },
  {
    id: "property_address",
    label: "Property Address",
    mandatory: true,
    checked: true,
  },
  { id: "created_by", label: "Created By", mandatory: true, checked: true },
  { id: "created_date", label: "Created Date", mandatory: false, checked: true },
  {
    id: "updated_date",
    label: "Updated Date",
    mandatory: false,
    checked: true,
  },
  {
    id: "cx_specialist_name",
    label: "CX Specialist",
    mandatory: false,
    checked: true,
  },
  {
    id: "application_status_name",
    label: "Status",
    mandatory: false,
    checked: true,
  },
  {
    id: "is_test_app",
    label: "Test Application",
    mandatory: false,
    checked: true,
  },
  {
    id: "submitted_to_crm",
    label: "Submitted to CRM",
    mandatory: false,
    checked: true,
  },
  {
    id: "is_submitted_by_partner_api",
    label: "Submitted by Partner API",
    mandatory: false,
    checked: true,
  },
  {
    id: "is_upside_submission",
    label: "Is Upside Submission",
    mandatory: false,
    checked: true,
  },
  {
    id: "is_upside_interested",
    label: "Is Upside Interested",
    mandatory: false,
    checked: true,
  },
  {
    id: "property_type",
    label: "Property Type",
    mandatory: false,
    checked: false,
  },
  {
    id: "company_name",
    label: "Company Name",
    mandatory: false,
    checked: false,
  },
  {
    id: "program_name",
    label: "Program Name",
    mandatory: false,
    checked: false,
  },
  {
    id: "purchase_price",
    label: "Purchase Price",
    mandatory: false,
    checked: false,
  },
  {
    id: "purchase_date",
    label: "Purchase Date",
    mandatory: false,
    checked: false,
  },
  {
    id: "owner_estimated_value",
    label: "Owner Estimated Value",
    mandatory: false,
    checked: false,
  },
  {
    id: "borrower_email",
    label: "Borrower Email",
    mandatory: false,
    checked: false,
  },
  {
    id: "co_borrower_name",
    label: "Co-Borrower Name",
    mandatory: false,
    checked: false,
  },
  {
    id: "co_borrower_email",
    label: "Co-Borrower Email",
    mandatory: false,
    checked: false,
  },
  {
    id: "loan_officer_name",
    label: "Loan Officer Name",
    mandatory: false,
    checked: false,
  },
  {
    id: "loan_officer_email",
    label: "Loan Officer Email",
    mandatory: false,
    checked: false,
  },
  {
    id: "real_estate_agent_name",
    label: "Real Estate Agent Name",
    mandatory: false,
    checked: false,
  },
  {
    id: "real_estate_agent_email",
    label: "Real Estate Agent Email",
    mandatory: false,
    checked: false,
  },
  {
    id: "partner_loan_number",
    label: "Partner Loan Number",
    mandatory: false,
    checked: false,
  },
  {
    id: "initial_submiter_role",
    label: "Initial Submitter Role",
    mandatory: false,
    checked: false,
  },
  {
    id: "initiail_submiter_name",
    label: "Initial Submitter Name",
    mandatory: false,
    checked: false,
  },
  {
    id: "initial_submiter_email",
    label: "Initial Submitter Email",
    mandatory: false,
    checked: false,
  },
  {
    id: "home_owner_approved",
    label: "Homeowner Approved",
    mandatory: false,
    checked: false,
  },
  {
    id: "home_owner_approval_date",
    label: "Homeowner Approval Date",
    mandatory: false,
    checked: false,
  },
  {
    id: "is_primary_residence",
    label: "Is Primary Residence",
    mandatory: false,
    checked: false,
  },
  {
    id: "equity_unlock_requested",
    label: "Equity Unlock Requested",
    mandatory: false,
    checked: false,
  },
];

const buildColumnsFromPreferences = (prefs) => {
  if (!prefs) {
    return INITIAL_COLUMNS.map((col, idx) => ({
      ...col,
      mandatory: MANDATORY_COLUMN_IDS.includes(col.id),
      checked: MANDATORY_COLUMN_IDS.includes(col.id) ? true : col.checked,
      orderIndex: idx,
    }));
  }

  const orderValues = Object.values(prefs)
    .map((p) => p?.order_index)
    .filter((n) => typeof n === "number");
  const maxOrder = orderValues.length ? Math.max(0, ...orderValues) : 0;

  const built = INITIAL_COLUMNS.map((col, idx) => {
    const isMandatory = MANDATORY_COLUMN_IDS.includes(col.id);
    const isFixedLeading = FIXED_LEADING_COLUMN_IDS.includes(col.id);
    const backendKey = COLUMN_BACKEND_KEY[col.id];
    const pref = backendKey ? prefs[backendKey] : null;
    const fixedLeadIndex = FIXED_LEADING_COLUMN_IDS.indexOf(col.id);

    if (isFixedLeading) {
      return {
        ...col,
        mandatory: true,
        checked: true,
        orderIndex: fixedLeadIndex,
        _prefOrder: fixedLeadIndex,
        _hasPrefOrder: true,
      };
    }

    const hasPrefOrder = typeof pref?.order_index === "number";
    // Columns with backend order_index sort by that value.
    // Mandatory columns missing from prefs stay visible but after ordered ones.
    const prefOrder = hasPrefOrder
      ? pref.order_index
      : isMandatory
        ? maxOrder + 1000 + idx
        : maxOrder + idx + 1;

    if (isMandatory) {
      return {
        ...col,
        mandatory: true,
        checked: true,
        _prefOrder: prefOrder,
        _hasPrefOrder: hasPrefOrder,
      };
    }

    if (pref) {
      return {
        ...col,
        mandatory: false,
        checked: !!pref.is_visible,
        _prefOrder: prefOrder,
        _hasPrefOrder: hasPrefOrder,
      };
    }

    return {
      ...col,
      mandatory: false,
      checked: false,
      _prefOrder: maxOrder + idx + 1,
      _hasPrefOrder: false,
    };
  });

  const fixed = built
    .filter((c) => FIXED_LEADING_COLUMN_IDS.includes(c.id))
    .sort((a, b) => a.orderIndex - b.orderIndex);

  const flexible = built
    .filter((c) => !FIXED_LEADING_COLUMN_IDS.includes(c.id))
    .sort((a, b) => {
      if (a._hasPrefOrder && !b._hasPrefOrder) return -1;
      if (!a._hasPrefOrder && b._hasPrefOrder) return 1;
      return a._prefOrder - b._prefOrder;
    })
    .map((c, i) => ({
      ...c,
      orderIndex: fixed.length + i,
    }));

  return [...fixed, ...flexible].map(
    ({ _prefOrder, _hasPrefOrder, ...col }) => col,
  );
};

const ApplicationListingTable = ({ props }) => {
  const {
    searchTerm,
    cxSpecialistValue,
    originatorValue,
    appStatus,
    currentPage,
    masterCheck,
    setMasterCheck,
    handlePaginationPageClick,
    advancedFilters,
    buildAdvancedFilterUrl,
    setSelectedInlineFilters,
    selectedInlineFilters,
    setTableData,
    pageSize,
    setPageSize,
    pendingInlineFilters,
    setPendingInlineFilters,
    filterApplyCount,
    showSnackbarMessage,
  } = props;

  const [tableDataLocal, setTableDataLocal] = useState([]);
  const [role, setRole] = useContext(UserRoleContext);
  const [forms, setForms] = useContext(FormContext);
  const navigate = useNavigate();
  const [isApplicationManager, setIsApplicationManager] = useState();
  const [totalRecords, setTotalRecords] = useState();
  const [recordMessage, setRecordMessage] = useState("");
  const [totalPages, setTotalPages] = useState(0);
  const [tableLoader, setTableLoader] = useState(false);
  const [activeFilterField, setActiveFilterField] = useState(null);
  const [selectedColumns, setSelectedColumns] = useState(() =>
    buildColumnsFromPreferences(getListingColumnPrefs()),
  );
  const preferenceDefaultColumns = useRef(
    buildColumnsFromPreferences(getListingColumnPrefs()),
  );

  const visibleOrderedColumns = selectedColumns
    .filter((c) => c.checked)
    .sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0));

  const [dragColId, setDragColId] = useState(null);
  const [dragOverColId, setDragOverColId] = useState(null);
  const [columnsDirty, setColumnsDirty] = useState(false);
  const [isSavingColumns, setIsSavingColumns] = useState(false);
  const [isResettingColumns, setIsResettingColumns] = useState(false);
  const [columnWidths, setColumnWidths] = useState(() =>
    buildColumnWidthsFromPreferences(getListingColumnPrefs()),
  );
  const [resizingColId, setResizingColId] = useState(null);
  const [sortField, setSortField] = useState("application_number");
  const [sortDirection, setSortDirection] = useState("desc");
  const resizeRef = useRef({ colId: null, startX: 0, startWidth: 0 });

  const buildHeaderCellStyle = (colId, canDrag, isDragOver, isDragged) => {
    const base = mergeColumnWidthIntoStyle(
      colId,
      getHeaderStickyStyle(colId, columnWidths),
      columnWidths,
    );
    return {
      ...base,
      position: base.position || "relative",
      // Cursor is now managed per-zone inside the header; the cell itself
      // shows "grab" only when actively dragging, otherwise "default"
      cursor: resizingColId ? "default" : canDrag ? "grab" : "default",
      outline: isDragOver ? "2px solid #067de2" : undefined,
      opacity: isDragged ? 0.55 : 1,
      userSelect: "none",
    };
  };

  const buildBodyCellStyle = (colId, stickyBg) =>
    mergeColumnWidthIntoStyle(
      colId,
      getBodyStickyStyle(colId, stickyBg, columnWidths),
      columnWidths,
    );

  const getStickyCellClassName = (colId, section) => {
    if (colId === "delete") {
      return section === "head"
        ? "app-listing-sticky-delete-head"
        : "app-listing-sticky-delete-body";
    }
    if (colId === "application_number") {
      return section === "head"
        ? "app-listing-sticky-appnum-head"
        : "app-listing-sticky-appnum-body";
    }
    return undefined;
  };

  const handleResizeStart = (e, colId) => {
    if (colId === "delete") return;
    e.preventDefault();
    e.stopPropagation();
    const startWidth = getColumnWidth(colId, columnWidths);
    resizeRef.current = { colId, startX: e.clientX, startWidth };
    setResizingColId(colId);
  };

  const handleResizeToggle = (e, colId) => {
    if (colId === "delete") return;
    e.preventDefault();
    e.stopPropagation();
    const defaultWidth = DEFAULT_COLUMN_WIDTHS[colId] ?? 120;
    const minWidth = getMinColumnWidth(colId);
    const currentWidth = getColumnWidth(colId, columnWidths);
    const isCollapsed = currentWidth <= minWidth + 4;
    setColumnWidths((prev) => ({
      ...prev,
      [colId]: isCollapsed ? defaultWidth : minWidth,
    }));
    setColumnsDirty(true);
  };

  useEffect(() => {
    if (!resizingColId) return;

    const onMouseMove = (e) => {
      const { colId, startX, startWidth } = resizeRef.current;
      if (!colId) return;
      const nextWidth = clampColumnWidth(startWidth + e.clientX - startX, colId);
      setColumnWidths((prev) => ({ ...prev, [colId]: nextWidth }));
    };

    const onMouseUp = () => {
      const resizedColId = resizeRef.current.colId;
      resizeRef.current = { colId: null, startX: 0, startWidth: 0 };
      setResizingColId(null);
      if (resizedColId) setColumnsDirty(true);
      document.body.classList.remove("column-resizing-active");
    };

    document.body.classList.add("column-resizing-active");
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      document.body.classList.remove("column-resizing-active");
    };
  }, [resizingColId]);

  const persistColumnOrderLocally = (prefs, rowPerPage = pageSize) => {
    try {
      const userInfo = JSON.parse(localStorage.getItem("user_info") || "{}");
      if (!userInfo.preferences) userInfo.preferences = {};
      userInfo.preferences.application_listing_columns = prefs;
      const n = Number(rowPerPage);
      if (Number.isFinite(n) && n > 0) {
        userInfo.preferences.application_listing_row_per_page = n;
      }
      localStorage.setItem("user_info", JSON.stringify(userInfo));
    } catch (e) {
      // ignore localStorage errors
    }
  };

  const saveColumnPreferences = async (columns, widths = columnWidths) => {
    const prefs = buildApplicationListingColumnPrefs(columns, widths);
    const rowPerPage = Number(pageSize) > 0 ? Number(pageSize) : 8;
    const userInfo = JSON.parse(localStorage.getItem("user_info") || "{}");
    const userId = userInfo?.id;
    const tokenStr = localStorage.getItem("access_token");

    if (!userId || !tokenStr) {
      throw new Error("User session not found. Please login again.");
    }

    const response = await fetch(`${config.baseUrl}user/${userId}/`, {
      method: "PATCH",
      headers: {
        Authorization: `Bearer ${tokenStr}`,
        "Content-Type": "application/json",
        accept: "application/json",
      },
      body: JSON.stringify({
        preferences: {
          application_listing_columns: prefs,
          application_listing_row_per_page: rowPerPage,
        },
      }),
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(
        data?.detail || data?.message || "Failed to save column preferences",
      );
    }

    // Prefer server preferences when returned; else use what we sent
    const savedPrefs =
      data?.preferences?.application_listing_columns ||
      data?.user_info?.preferences?.application_listing_columns ||
      prefs;

    const savedRowPerPage = Number(
      data?.preferences?.application_listing_row_per_page ??
      data?.user_info?.preferences?.application_listing_row_per_page ??
      rowPerPage,
    );

    persistColumnOrderLocally(
      savedPrefs,
      Number.isFinite(savedRowPerPage) && savedRowPerPage > 0
        ? savedRowPerPage
        : rowPerPage,
    );
    setColumnWidths(buildColumnWidthsFromPreferences(savedPrefs));
    if (Number.isFinite(savedRowPerPage) && savedRowPerPage > 0) {
      setPageSize(savedRowPerPage);
    }

    if (data?.preferences) {
      userInfo.preferences = {
        ...(userInfo.preferences || {}),
        ...data.preferences,
      };
      localStorage.setItem("user_info", JSON.stringify(userInfo));
    } else if (data?.id) {
      localStorage.setItem("user_info", JSON.stringify({ ...userInfo, ...data }));
    }

    preferenceDefaultColumns.current = columns.map((c) => ({ ...c }));
    setColumnsDirty(false);
    return data;
  };

  const ensureMandatoryColumnsVisible = (columns) =>
    columns.map((col) =>
      MANDATORY_COLUMN_IDS.includes(col.id)
        ? { ...col, checked: true, mandatory: true }
        : col,
    );

  const handleColumnsChange = (nextOrFn) => {
    setSelectedColumns((prev) => {
      const next = typeof nextOrFn === "function" ? nextOrFn(prev) : nextOrFn;
      return ensureMandatoryColumnsVisible(next);
    });
    setColumnsDirty(true);
  };

  const handleSaveTable = async () => {
    if (isSavingColumns) return;
    setIsSavingColumns(true);
    try {
      const nextColumns = ensureMandatoryColumnsVisible(selectedColumns);
      setSelectedColumns(nextColumns);
      await saveColumnPreferences(nextColumns);
    } catch (err) {
      alert(err?.message || "Failed to save table columns");
    } finally {
      setIsSavingColumns(false);
    }
  };

  const handlePersistColumns = async (columns) => {
    const nextColumns = ensureMandatoryColumnsVisible(columns);
    setSelectedColumns(nextColumns);
    if (isSavingColumns) return;
    setIsSavingColumns(true);
    try {
      await saveColumnPreferences(nextColumns);
    } catch (err) {
      setColumnsDirty(true);
      alert(err?.message || "Failed to save column preferences");
    } finally {
      setIsSavingColumns(false);
    }
  };

  const resetColumnPreferences = async () => {
    const userInfo = JSON.parse(localStorage.getItem("user_info") || "{}");
    const userId = userInfo?.id;
    const tokenStr = localStorage.getItem("access_token");

    if (!userId || !tokenStr) {
      throw new Error("User session not found. Please login again.");
    }

    const response = await fetch(
      `${config.baseUrl}user/${userId}/reset-aous-columns/`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${tokenStr}`,
          accept: "application/json",
        },
      },
    );

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(
        data?.detail || data?.message || "Failed to reset column preferences",
      );
    }

    const resetUserInfo = data?.user_info || userInfo;
    localStorage.setItem("user_info", JSON.stringify(resetUserInfo));

    const prefs =
      resetUserInfo?.preferences?.application_listing_columns || null;
    const resetColumns = buildColumnsFromPreferences(prefs);

    setSelectedColumns(resetColumns);
    setColumnWidths(buildColumnWidthsFromPreferences(prefs));
    preferenceDefaultColumns.current = resetColumns.map((c) => ({ ...c }));
    const resetRowPerPage = getListingRowPerPage(8);
    // Prefer value from reset response if present
    const fromReset = Number(
      resetUserInfo?.preferences?.application_listing_row_per_page,
    );
    setPageSize(
      Number.isFinite(fromReset) && fromReset > 0 ? fromReset : resetRowPerPage,
    );
    setColumnsDirty(false);

    if (data?.message && showSnackbarMessage) {
      showSnackbarMessage(data.message, "success");
    }

    return resetColumns;
  };

  const handleResetColumns = async () => {
    if (isSavingColumns || isResettingColumns) return;
    setIsResettingColumns(true);
    try {
      return await resetColumnPreferences();
    } catch (err) {
      if (showSnackbarMessage) {
        showSnackbarMessage(err?.message || "Failed to reset columns", "error");
      } else {
        alert(err?.message || "Failed to reset columns");
      }
      throw err;
    } finally {
      setIsResettingColumns(false);
    }
  };

  const isColumnNonDraggable = (colId) =>
    colId === "delete" || colId === "application_number";

  const handleColumnDragStart = (e, colId) => {
    if (isColumnNonDraggable(colId)) {
      e.preventDefault();
      return;
    }
    setDragColId(colId);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", colId);
  };

  const handleColumnDragOver = (e, colId) => {
    if (isColumnNonDraggable(colId)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverColId !== colId) setDragOverColId(colId);
  };

  const handleColumnDrop = (e, dropColId) => {
    e.preventDefault();
    const fromId = e.dataTransfer.getData("text/plain") || dragColId;
    if (!fromId || isColumnNonDraggable(dropColId)) {
      setDragColId(null);
      setDragOverColId(null);
      return;
    }
    setSelectedColumns((prev) => reorderColumnsByDrag(prev, fromId, dropColId));
    setColumnsDirty(true);
    setDragColId(null);
    setDragOverColId(null);
  };

  const handleColumnDragEnd = () => {
    setDragColId(null);
    setDragOverColId(null);
  };

  const appliedOnceRef = useRef(false);
  const initialLoadDoneRef = useRef(false);
  const fetchRequestIdRef = useRef(0);

  useEffect(() => {
    fetchData();
  }, [
    currentPage,
    originatorValue,
    appStatus,
    cxSpecialistValue,
    advancedFilters,
    pageSize,
    searchTerm,
    selectedInlineFilters,
    filterApplyCount,
  ]);

  useEffect(() => {
    if (filterApplyCount > 0) {
      appliedOnceRef.current = true;
    }
  }, [filterApplyCount]);

  useEffect(() => {
    if (Object.keys(selectedInlineFilters).length === 0 && filterApplyCount === 0) {
      appliedOnceRef.current = false;
    }
  }, [selectedInlineFilters, filterApplyCount]);

  useEffect(() => {
    let userInfo = localStorage.getItem("user_info");
    let parseInfo = JSON.parse(userInfo);
    let isManager;
    if (role !== null) {
      if (parseInfo.id === role.user_info.id) {
        isManager = role.user_info.groups.some(
          (group) => group.name === "Application Manager",
        );
      }
    } else {
      isManager = parseInfo.groups.some(
        (group) => group.name === "Application Manager",
      );
    }
    setIsApplicationManager(isManager);
  }, []);

  const fetchData = async (
    filtersOverride = null,
    pageOverride = null,
    pendingOverride = null,
    sortOverride = null,
  ) => {
    const effectivePage = pageOverride ?? currentPage;
    // Full-table skeleton only on the very first load — never again after that
    // (empty filter results used to retrigger skeleton and look "stuck")
    const showLoader = !initialLoadDoneRef.current;
    if (showLoader) {
      setTableLoader(true);
    }

    const requestId = ++fetchRequestIdRef.current;
    var tokenStr = localStorage.getItem("access_token");
    let url = `${config.baseUrl}filter-trade-in-application/?page=${effectivePage}&page_size=${pageSize || 8}`;

    if (searchTerm.trim() !== "") {
      url += `&search=${searchTerm}`;
    }

    if (originatorValue !== "all") {
      url += `&originator_name_id=${originatorValue}`;
    }

    if (appStatus !== "all") {
      url += `&application_status_id=${appStatus}`;
    }

    if (cxSpecialistValue !== "all") {
      url += `&cx_specialist_id=${cxSpecialistValue}`;
    }

    const mergedInlineFilters =
      filtersOverride !== null || pendingOverride !== null
        ? {
          ...(pendingOverride ?? pendingInlineFilters),
          ...(filtersOverride ?? selectedInlineFilters),
        }
        : {
          ...pendingInlineFilters,
          ...selectedInlineFilters,
        };

    url = buildAdvancedFilterUrl(url, advancedFilters, mergedInlineFilters);

    const effectiveSortField = sortOverride?.field ?? sortField;
    const effectiveSortDirection = sortOverride?.direction ?? sortDirection;
    const sortOrderingNum =
      effectiveSortField && COLUMN_ORDERING_KEY[effectiveSortField];
    if (sortOrderingNum && effectiveSortDirection) {
      const orderingParam =
        effectiveSortDirection === "desc" ? -sortOrderingNum : sortOrderingNum;
      url += `&ordering=${orderingParam}`;
    }

    try {
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${tokenStr}` },
      });
      const result = await response.json().catch(() => ({}));

      // Ignore outdated responses when a newer request already started
      if (requestId !== fetchRequestIdRef.current) return;

      if (response.status === 200 || response.status === 201) {
        const rows = Array.isArray(result?.results) ? result.results : [];
        setTableDataLocal(rows);
        if (setTableData) {
          setTableData(rows);
        }
        const count = Number(result?.count) || 0;
        const pages = Math.max(1, Math.ceil(count / (pageSize || 8)));
        setTotalRecords(count);
        setTotalPages(count === 0 ? 0 : pages);
        setRecordMessage(count === 0 ? "No Records Found" : "");
        initialLoadDoneRef.current = true;
        // If current page is past the last page after filter/sort, move to last page
        if (count > 0 && effectivePage > pages) {
          handlePaginationPageClick(pages);
        }
      } else if (response.status === 403) {
        localStorage.setItem("access_token", "");
        navigate("/");
      }
    } catch (error) {
      console.log(error);
    } finally {
      if (requestId === fetchRequestIdRef.current) {
        setTableLoader(false);
      }
    }
  };

  const IsChecked = (id) => {
    if (!localTemp || localTemp.length === 0) return false;
    let isCkd = localTemp ? localTemp?.find((item) => item === id) : false;
    return isCkd;
  };

  const onMasterCheck = (e, id) => {
    if (e.target.checked === true) {
      localTemp.push(id);
      var duplicate = [...localTemp];
      setMasterCheck(duplicate);
    } else {
      for (let i = 0; i < localTemp.length; i++) {
        if (id === localTemp[i]) {
          localTemp.splice(i, 1);
        }
      }
      var duplicate2 = [...localTemp];
      setMasterCheck(duplicate2);
    }
  };

  const handlePaginationChange = (e, page) => {
    const pageNumber = parseInt(page);
    if (!isNaN(pageNumber)) {
      handlePaginationPageClick(pageNumber);
    } else {
      console.error("Invalid page number:", page);
    }
  };

  function handleOpenApplication(id) {
    // setLoderStatus(!loaderStatus)
    localStorage.setItem("LoName", null);
    localStorage.setItem("RsaName", null);
    var tokenStr = localStorage.getItem("access_token");
    fetch(`${config.baseUrl}trade-in-application/${id}/`, {
      headers: { Authorization: `Bearer ${tokenStr}` },
    })
      .then(async (response) => {
        const data = await response.json();
        if (response.status === 200 || response.status === 201) {
          setForms(data);
          navigate("/application-create", { state: { applicationData: data } });
          // setLoderStatus(!loaderStatus)
        } else if (response.status === 403) {
          localStorage.setItem("access_token", "");
          navigate("/");
        }
      })
      .catch((err) => {
        console.error(err);
      });
  }

  const handleApplyInlineFilter = (key, values) => {
    appliedOnceRef.current = true;
    // Keep current page in the API payload (do not reset to page 1)
    setSelectedInlineFilters((prev) => ({ ...prev, [key]: values }));
    // Keep pending in sync so reopening popup / chips keep has_date + range
    setPendingInlineFilters((prev) => ({ ...prev, [key]: values }));
    handlePaginationPageClick(1);
  };

  const handleColumnSort = (colId) => {
    if (!COLUMN_ORDERING_KEY[colId]) return;
    const nextField = colId;
    const nextDirection =
      sortField === colId && sortDirection === "asc" ? "desc" : "asc";
    setSortField(nextField);
    setSortDirection(nextDirection);
    // Reset to page 1 on sort instead of keeping current page
    handlePaginationPageClick(1);
    fetchData(selectedInlineFilters, 1, pendingInlineFilters, {
      field: nextField,
      direction: nextDirection,
    });
  };

  const handleClearInlineFilter = (key) => {
    const cleanedPending = { ...pendingInlineFilters };
    delete cleanedPending[key];

    const cleanedSelected = { ...selectedInlineFilters };
    delete cleanedSelected[key];

    setPendingInlineFilters(cleanedPending);
    setSelectedInlineFilters(cleanedSelected);

    if (!appliedOnceRef.current) return;
  };

  return (
    <>
      {tableLoader ? (
        <TableContainer
          component={Paper}
          className="app-listing-table-container"
          sx={{
            border: "1px solid black",
            marginTop: "1em",
            borderRadius: 0,
          }}
        >
          <Table sx={{ minWidth: 700 }} aria-label="customized table">
            <TableHead>
              <TableRow>
                <TableCell>
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    height="60px"
                    animation="wave"
                  />
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {new Array(8).fill(0).map(() => {
                return (
                  <TableRow>
                    <TableCell colSpan={1}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Skeleton
                          variant="rectangular"
                          width="3%"
                          height="40px"
                          animation="wave"
                        />
                        <Skeleton
                          variant="rectangular"
                          width="96%"
                          height="40px"
                          animation="wave"
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <>
          <ApplicationListingColumnSelector
            columns={selectedColumns}
            setColumns={handleColumnsChange}
            defaultColumns={preferenceDefaultColumns.current}
            onSaveTable={handleSaveTable}
            onPersistColumns={handlePersistColumns}
            onResetColumns={handleResetColumns}
            columnsDirty={columnsDirty}
            isSavingColumns={isSavingColumns}
            isResettingColumns={isResettingColumns}
          />
          <TableContainer
            component={Paper}
            className="app-listing-table-container"
            sx={{
              border: "1px solid black",
              overflowX: "auto",
              borderRadius: 0,
            }}
          >
            <Table
              sx={{
                minWidth: 700,
                minWidth: "max-content",
                borderCollapse: "separate",
                borderSpacing: 0,
              }}
              aria-label="customized-table"
            >
              <TableHead>
                <TableRow>
                  {visibleOrderedColumns.map((col) => {
                    const filterCfg = COLUMN_FILTER_CONFIG[col.id];
                    const canDrag = !isColumnNonDraggable(col.id);
                    const isDragOver =
                      dragOverColId === col.id &&
                      dragColId &&
                      dragColId !== col.id;
                    return (
                      <StyledTableCell
                        key={col.id}
                        align="center"
                        className={getStickyCellClassName(col.id, "head")}
                        style={buildHeaderCellStyle(
                          col.id,
                          canDrag,
                          isDragOver,
                          dragColId === col.id,
                        )}
                        draggable={canDrag && !resizingColId}
                        onDragStart={(e) => handleColumnDragStart(e, col.id)}
                        onDragOver={(e) => handleColumnDragOver(e, col.id)}
                        onDrop={(e) => handleColumnDrop(e, col.id)}
                        onDragEnd={handleColumnDragEnd}
                      >
                        {col.id === "delete" ? (
                          <ColumnHeaderLabel label="Delete" />
                        ) : (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "stretch",
                              width: "100%",
                              minWidth: 0,
                              overflow: "hidden",
                            }}
                          >
                            {/* ── Zone 1: Filter icon ───────────────────────────
                                Fixed-width, cursor: pointer, triggers filter.   */}
                            {filterCfg && (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexShrink: 0,
                                  flexGrow: 0,
                                  minWidth: "28px",
                                  px: "5px",
                                  py: "4px",
                                  borderRadius: "4px",
                                  cursor: "pointer",
                                  transition:
                                    "background-color 0.2s ease, box-shadow 0.2s ease",
                                  backgroundColor:
                                    activeFilterField === col.id
                                      ? "rgba(6, 125, 226, 0.12)"
                                      : "transparent",
                                  "&:hover": {
                                    backgroundColor: "rgba(9, 72, 128, 0.08)",
                                    boxShadow:
                                      "0 1px 4px rgba(11, 52, 88, 0.25)",
                                  },
                                  "&:hover .app-listing-filter-icon": {
                                    color: "#067de2",
                                  },
                                }}
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setActiveFilterField(
                                    activeFilterField === col.id
                                      ? null
                                      : col.id,
                                  );
                                }}
                              >
                                <TuneIcon
                                  className="app-listing-filter-icon"
                                  sx={{
                                    fontSize: "18px",
                                    color:
                                      activeFilterField === col.id
                                        ? "#067de2"
                                        : "rgb(85, 85, 85)",
                                    transition: "color 0.2s",
                                    display: "block",
                                    pointerEvents: "none",
                                  }}
                                />
                              </Box>
                            )}

                            {/* ── Zone 2: Column name ───────────────────────────
                                Grows/shrinks with available text space.
                                cursor: grab, triggers sort on click.            */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flex: "1 1 0%",   // grow/shrink but start from 0 so
                                // it never pushes into zone 3
                                minWidth: 0,
                                px: "4px",
                                py: "4px",
                                borderRadius: "4px",
                                cursor: COLUMN_ORDERING_KEY[col.id]
                                  ? "grab"
                                  : "default",
                                "&:hover": COLUMN_ORDERING_KEY[col.id]
                                  ? { backgroundColor: "rgba(0,0,0,0.04)" }
                                  : {},
                              }}
                              onMouseDown={(e) => {
                                if (COLUMN_ORDERING_KEY[col.id])
                                  e.stopPropagation();
                              }}
                              onClick={(e) => {
                                if (COLUMN_ORDERING_KEY[col.id]) {
                                  e.stopPropagation();
                                  handleColumnSort(col.id);
                                }
                              }}
                            >
                              <ColumnHeaderLabel label={col.label} />
                            </Box>

                            {/* ── Zone 3: Sort icon ─────────────────────────────
                                Fixed width, fully independent from zone 2.
                                cursor: pointer, triggers sort on click.         */}
                            {COLUMN_ORDERING_KEY[col.id] && (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexShrink: 0,
                                  flexGrow: 0,
                                  minWidth: "28px",
                                  px: "5px",
                                  py: "4px",
                                  borderRadius: "4px",
                                  cursor: "pointer",
                                  "&:hover": {
                                    backgroundColor: "rgba(6, 125, 226, 0.08)",
                                  },
                                }}
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleColumnSort(col.id);
                                }}
                              >
                                <ColumnSortToggle
                                  colId={col.id}
                                  sortField={sortField}
                                  sortDirection={sortDirection}
                                  onSort={() => { }}
                                />
                              </Box>
                            )}

                            {/* Filter popup — floats above the table */}
                            {filterCfg && activeFilterField === col.id && (
                              <InlineFilterPopup
                                fieldKey={col.id}
                                fieldType={filterCfg.fieldType || "text"}
                                textFilterVariant={
                                  filterCfg.textFilterVariant
                                }
                                hideExactOn={!!filterCfg.hideExactOn}
                                showHasDateBoolean={
                                  !!filterCfg.showHasDateBoolean
                                }
                                showNotAvailableOption={!!filterCfg.showNotAvailableOption}
                                defaultValues={(() => {
                                  const selected =
                                    selectedInlineFilters?.[col.id] || {};
                                  const pending =
                                    pendingInlineFilters?.[col.id];
                                  if (pending !== undefined) {
                                    return {
                                      ...(pending || {}),
                                      ...(pending?.has_date === undefined &&
                                        selected.has_date !== undefined
                                        ? { has_date: selected.has_date }
                                        : {}),
                                    };
                                  }
                                  return selected;
                                })()}
                                onApply={handleApplyInlineFilter}
                                onClear={handleClearInlineFilter}
                                onClose={() => setActiveFilterField(null)}
                                onPendingChange={(key, values) =>
                                  setPendingInlineFilters((prev) => ({
                                    ...prev,
                                    [key]: values,
                                  }))
                                }
                              />
                            )}
                          </Box>
                        )}
                        {col.id !== "delete" && (
                          <Box
                            className={`column-resize-handle${resizingColId === col.id ? " column-resize-active" : ""
                              }`}
                            onMouseDown={(e) => handleResizeStart(e, col.id)}
                            onDoubleClick={(e) => handleResizeToggle(e, col.id)}
                            title="Drag border to resize. Double-click to expand/collapse."
                          />
                        )}
                      </StyledTableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {tableDataLocal.map((row, rowIndex) => {
                  const stickyBg = rowIndex % 2 === 1 ? "#f4f5f6" : "#ffffff";
                  return (
                    <StyledTableRow key={row.trade_in_application_number}>
                      {visibleOrderedColumns.map((col) => {
                        if (col.id === "delete") {
                          return (
                            <StyledTableCell
                              key={col.id}
                              align="center"
                              className={getStickyCellClassName(col.id, "body")}
                              style={buildBodyCellStyle(col.id, stickyBg)}
                            >
                              <input
                                type="checkbox"
                                className="form-check-input"
                                id="del_checkbox"
                                disabled={
                                  isApplicationManager === true
                                    ? false
                                    : row?.is_test_app === true
                                      ? false
                                      : true
                                }
                                style={{ cursor: "pointer" }}
                                defaultChecked={
                                  masterCheck.length === 0
                                    ? null
                                    : IsChecked(row.trade_in_application_id)
                                }
                                key={row.trade_in_application_id}
                                onClick={(e) =>
                                  onMasterCheck(e, row.trade_in_application_id)
                                }
                              />
                            </StyledTableCell>
                          );
                        }
                        if (col.id === "application_number") {
                          return (
                            <StyledTableCell
                              key={col.id}
                              align="center"
                              title={row.trade_in_application_number || undefined}
                              onClick={() => {
                                handleOpenApplication(
                                  row.trade_in_application_id,
                                );
                              }}
                              className={`individual-application-row ${getStickyCellClassName(col.id, "body") || ""}`.trim()}
                              style={buildBodyCellStyle(col.id, stickyBg)}
                            >
                              {row.trade_in_application_number}
                            </StyledTableCell>
                          );
                        }
                        const cellAlign = getBodyCellAlign(col.id);
                        const baseStyle = buildBodyCellStyle(col.id, stickyBg);
                        const cellStyle =
                          col.id === "property_address"
                            ? { ...baseStyle, overflow: "visible" }
                            : baseStyle;
                        return (
                          <StyledTableCell
                            key={col.id}
                            align={cellAlign}
                            className={
                              col.id === "property_address"
                                ? "app-listing-property-address-td"
                                : undefined
                            }
                            style={cellStyle}
                          >
                            {DATE_TIME_COLUMN_IDS.includes(col.id) ? (
                              <DateTimeCell
                                dateValue={getDateRawValue(col.id, row)}
                                emptyLabel={
                                  col.id === "created_date" ||
                                    col.id === "updated_date"
                                    ? "NA"
                                    : "-"
                                }
                              />
                            ) : (
                              <CellWithTitle
                                value={getColumnCellValue(col.id, row)}
                                className={
                                  col.id === "property_address"
                                    ? "app-listing-truncated-cell app-listing-property-address-cell"
                                    : "app-listing-truncated-cell"
                                }
                              />
                            )}
                          </StyledTableCell>
                        );
                      })}
                    </StyledTableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <div
        className="app-listing-pagination-container"
        style={{
          display: "flex",
          position: "relative",
          justifyContent: "flex-start",
          alignItems: "center",
          marginTop: "15px",
          flexDirection: "row",
          width: "100vw",
        }}
      >
        {/* Left: Rows per page */}
        <div style={{ position: "absolute", left: 0, marginRight: "20%" }}>
          <span style={{ marginRight: "10px", fontSize: "14px" }}>
            Rows per page:
          </span>
          <input
            type="number"
            min="1"
            value={pageSize === "" ? "" : pageSize}
            placeholder="Rows"
            onChange={(e) => {
              const val = e.target.value;
              if (val === "") {
                setPageSize("");
                setColumnsDirty(true);
                return;
              }
              const num = Number(val);
              if (num > 0) {
                setPageSize(num);
                setColumnsDirty(true);
                handlePaginationPageClick(1);
              }
            }}
            style={{
              width: "60px",
              padding: "5px",
              borderRadius: "4px",
            }}
          />
        </div>

        {/* Right: Pagination + total text */}
        {totalRecords !== 0 && !tableLoader ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Pagination
              count={totalPages}
              boundaryCount={2}
              siblingCount={1}
              variant="outlined"
              page={currentPage}
              onChange={(e, page) => handlePaginationChange(e, page)}
              className="app-listing-pagination"
            />
            <span className="total-pages-text">
              {totalRecords === 0
                ? "0"
                : `${(currentPage - 1) * pageSize + 1} - ${Math.min(
                  currentPage * (pageSize || 8),
                  totalRecords,
                )}`}{" "}
              of {totalRecords}
            </span>
          </div>
        ) : (
          !tableLoader && <span id="record-message">{recordMessage}</span>
        )}
      </div>
    </>
  );
};

export default ApplicationListingTable;
